/**
 * @file previousRange.ts
 * @brief The window just before a report's range, same length, for the KPI
 *        deltas (DES 03-10-2026: reports get the dashboard's +/-% pills).
 *
 * @details
 * A report's range is any span the user picks, so its comparison is the
 * span of equal length that ends where it starts: 1 to 30 Sep compares with
 * 2 to 31 Aug. `sumInCurrency` keeps money deltas honest: only rows in the
 * currency the KPI shows are added up on both sides.
 *
 * @author DES
 * @date 03-10-2026
 */

export interface IsoRange {
  startDate?: string;
  endDate?: string;
}

/**
 * Same length, ending 1 ms before `range` starts. An open-ended range has
 * no "before", so it comes back as is (the delta then reads 0%).
 */
export function previousRange(range: IsoRange): { startDate?: string; endDate?: string } {
  if (!range.startDate || !range.endDate) {
    return { startDate: range.startDate, endDate: range.endDate };
  }
  const start = new Date(range.startDate).getTime();
  const end = new Date(range.endDate).getTime();
  const span = Math.max(0, end - start);
  const prevEnd = start - 1;
  return {
    startDate: new Date(prevEnd - span).toISOString(),
    endDate: new Date(prevEnd).toISOString(),
  };
}

/** "vs the 30 days before" (rounded to whole days, at least 1). */
export function previousRangeLabel(range: IsoRange): string {
  if (!range.startDate || !range.endDate) return "";
  const days = Math.max(
    1,
    Math.round((new Date(range.endDate).getTime() - new Date(range.startDate).getTime()) / 86_400_000),
  );
  return days === 1 ? "vs the day before" : `vs the ${days} days before`;
}

/** Sum a field over the rows in one currency (rows without one count as it). */
export function sumInCurrency<T extends { currency?: string | null }>(
  rows: readonly T[],
  currency: string,
  pick: (row: T) => number,
): number {
  return rows
    .filter((r) => (r.currency || currency) === currency)
    .reduce((sum, r) => sum + (Number(pick(r)) || 0), 0);
}
